import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { useMutation, useClient } from 'Data/Api'
import { useDataValue } from 'Simple/Data'
import {
  notifyError,
  useNotifications,
  notifySuccess,
} from 'Logic/Notifications.js'
import { phoneNumber } from 'Data/validate.js'
import mutationInsertContactMethodAssociation from './mutation-insert-contact-method-association.graphql.js'
import mutationUpdateOtherContactMethodAssociations from './mutation-update-other-contact-method-associations.graphql.js'
import query from './query.graphql.js'

export default function useDataOnSubmit(props, data) {
  let onActionUpdatePhoneForPatient =
    useDataOnActionUpdatePhoneForPatient(props)
  let onActionFormSubmit = useDataOnActionFormSubmit(props)

  return async function onSubmit({ value, originalValue, args }) {
    switch (args?.type) {
      case 'update_phone': {
        return onActionUpdatePhoneForPatient({ value, originalValue, args })
      }
      default: {
        return onActionFormSubmit({ value, originalValue, args })
      }
    }
  }
}

function useDataOnActionFormSubmit(props) {
  let client = useClient()
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationInsertContactMethodAssociation] = useMutation(
    mutationInsertContactMethodAssociation
  )

  let person_id = useDataValue({
    viewPath: props.viewPath,
    context: 'profile_patient',
    path: 'person.id',
  })

  return async function onAction({ value, originalValue, args }) {
    if (!phoneNumber(value.phone.number)) {
      return true
    }

    // if the phone was edited, check if other people are associated with the old phone number
    if (value.phone.number !== originalValue.phone.number) {
      let { data } = await client
        .query(query, {
          contact_method_id: value.contact_method_id,
          person_id,
        })
        .toPromise()

      if (
        data?.vaxiom_contact_method_associations_aggregate?.aggregate.count > 0
      ) {
        setFlowTo(normalizePath(props.viewPath, './EditOrAdd/Content'))
        return
      }
    }

    let mutationEditPersonContactMehtodResponse =
      await executeMutationInsertContactMethodAssociation({
        objects: [getVariables(value)],
      })

    if (mutationEditPersonContactMehtodResponse.error) {
      notify(notifyError('Something went wrong. Please, try again.'))
      return
    }

    notify(notifySuccess('Phone updated!'))
    setFlowTo(normalizePath(props.viewPath, '../No'))
  }
}

function useDataOnActionUpdatePhoneForPatient(props) {
  let ids = useDataValue({
    context: 'selected',
    path: 'contact_method_association_ids',
    viewPath: props.viewPath,
  })

  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, notify] = useNotifications()

  let [, executeMutationInsertContactMethodAssociation] = useMutation(
    mutationInsertContactMethodAssociation
  )
  let [, executeMutationUpdateOtherContactMethodAssociations] = useMutation(
    mutationUpdateOtherContactMethodAssociations
  )

  return async function onAction({ value }) {
    let mutationResponseInsertContactMethodAssociation =
      await executeMutationInsertContactMethodAssociation({
        objects: [getVariables(value)],
      })

    if (mutationResponseInsertContactMethodAssociation.error) {
      notify(
        notifyError(
          'Something went wrong. Please, try again or contact support if the problem persists.'
        )
      )
      return
    }

    let contact_method_id =
      mutationResponseInsertContactMethodAssociation.data
        .insert_contact_method_associations[0].contact_method_id

    let mutationUpdateOtherContactMethodAssociationsResponse =
      await executeMutationUpdateOtherContactMethodAssociations({
        ids,
        contact_method_id,
      })

    if (mutationUpdateOtherContactMethodAssociationsResponse.error) {
      notify(notifyError('Something went wrong. Please, try again.'))
      return
    }

    notify(notifySuccess('Phone saved!'))

    setFlowTo(normalizePath(props.viewPath, '../No'))
    return
  }
}

function getVariables(value) {
  return {
    id: value.id,
    person_id: value.person_id,
    preference: value.preference ? 'ANY' : null,
    description: value.description,
    contact_method: {
      id: value.phone.id,
      dtype: 'phone',
      number: value.phone.number,
      type: value.phone.type,
    },
    communication_preference: value.communication_preferences
      ? {
          id: value.communication_preferences.id,
          patient_id: value.communication_preferences.patient_id,
          type: value.communication_preferences.type
            ? 'APPOINTMENT_MESSAGES'
            : null,
        }
      : null,
  }
}
